import {
  Avatar,
  Box,
  Popover,
  Stack,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import {
  AccountIcon,
  CheckIcon,
  ClipboardCheckIcon,
  DomainIcon,
  FaxIcon,
  PhoneIcon,
} from '@atgfr8/icons';
import { DuplicateCarrierIndicator } from '@components';
import { FeatureFlags } from '@constants/featureFlags';
import { userDetailsUrl } from '@constants/urls';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { extractRelativePath } from '@store/userDetails';
import { formatPhoneNumber } from '@utils/phoneNumberFormat';
import { createSignal, Show } from 'solid-js';

import PointyCheck from '../../../../assets/PointyCheck.svg';
import PointyX from '../../../../assets/PointyX.svg';
import {
  CarrierType,
  CustomerType,
  FactoringCompanyType,
  OfficeType,
  PhoneType,
  PickupType,
  ProspectType,
  ProType,
  ReferenceType,
  ShipmentMetadataType,
  TrackingType,
  UserType,
  VendorRendererPropsT,
} from '../types';

export const ReferenceItemsRender = (props: ReferenceType) => {
  return (
    <Stack>
      <Typography variant="caption" class="font-bold!">
        Order #{props.orderId}
      </Typography>
      <Typography variant="caption" class="text-[#333]!">
        {props.customer}
      </Typography>
      <Typography variant="caption" class="text-[#333]!">
        Ref: {props.referenceNum}
      </Typography>
      <Typography variant="caption" class="text-[#333]!">
        ShipmentId: {props.shipmentId ? props.shipmentId : 'N/A'}
      </Typography>
    </Stack>
  );
};

export const ShipmentMetaDataItemsRender = (props: ShipmentMetadataType) => {
  return (
    <Stack>
      <Typography variant="caption" class="font-bold!">
        Order #{props.orderId}
      </Typography>
      <Stack direction="row" spacing={1}>
        <Typography variant="caption" class="text-[#333]!">
          {props.metadataFieldName}:
        </Typography>
        <Typography variant="caption" class="text-[#333]!">
          {props.metadataFieldValue}
        </Typography>
      </Stack>
    </Stack>
  );
};

export const CarrierItemsRender = (props: CarrierType) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      class="w-full"
    >
      <Stack>
        <Stack direction="row" spacing={1}>
          <Typography variant="caption" class="font-bold!">
            {props.name}
          </Typography>
          {props.ltlEnabled && (
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              class="px-1 rounded-xs"
              backgroundColor="#dff0d8"
            >
              <Typography variant="caption" class="font-semibold">
                LTL
              </Typography>
              <CheckIcon size={14} color="darkgreen" />
            </Stack>
          )}
        </Stack>

        <Typography variant="caption" class="text-[#333]">
          MC: {props.mc || 'N/A'} | DOT:
          {props.dot || 'N/A'}
        </Typography>

        <Typography variant="caption" class="text-[#333]">
          City: {props.city || 'N/A'} | State:
          {props.state || 'N/A'}
        </Typography>

        <Typography variant="caption" class="text-[#333]">
          Status: <span class="font-bold!">{props.status}</span>
        </Typography>
      </Stack>
      <Show when={props.isDuplicate}>
        <DuplicateCarrierIndicator id={props.id} />
      </Show>
    </Stack>
  );
};

export const CustomerItemsRender = (props: CustomerType) => {
  return (
    <Stack class="w-full">
      <Stack
        direction={'row'}
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="caption" class="font-bold!">
          {props.name}
        </Typography>
        <Typography
          variant="caption"
          sxProps={{
            color: props.status !== 'Customer' ? 'red' : 'green',
            fontWeight: 'bold',
          }}
        >
          {props.status}
        </Typography>
      </Stack>

      <Stack direction="row" justifyContent="space-between">
        <Stack direction="row">
          <DomainIcon class="text-[16px]! align-baseline!" />
          <Typography variant="caption" class="text-[#333]!">
            : {props.office}
          </Typography>
        </Stack>
        <Show when={props.subStatus}>
          <Typography variant="caption" class="text-gray-400! text-xs!">
            {props.subStatus}
          </Typography>
        </Show>
      </Stack>

      <Stack direction="row">
        <AccountIcon class="text-[16px]!  align-baseline!" />
        <Typography variant="caption" class="text-[#333]!">
          : {props.accountManager}
        </Typography>
      </Stack>
    </Stack>
  );
};

export const VendorItemsRenderer = (props: VendorRendererPropsT) => {
  const canManageBilling = isFeatureFlagEnabled(
    FeatureFlags.AbleToManageVendorBilling,
  );

  return (
    <Stack class="w-full">
      <Box
        style={{
          display: 'flex',
          'align-items': 'center',
          'justify-content': 'space-between',
        }}
      >
        <Box fontSize="16px" fontWeight={500} class="leading-[150%]">
          {props.vendor.name}
        </Box>
        <Show when={props.vendor.accountingSysVendorId}>
          <ClipboardCheckIcon color="#28957C" />
        </Show>
      </Box>
      <Box class="text-[#5d6062]">
        <Show when={props.vendor.addressLine1}>
          <Box class="flex flex-row">
            <span class="pr-1">{props.vendor.addressLine1}</span>
            {Boolean(props.vendor.addressLine2) && (
              <span class="pr-1">{props.vendor.addressLine2}</span>
            )}
            <span>
              {props.vendor.city}, {props.vendor.stateProvince}{' '}
              {props.vendor.postalCode}
            </span>
          </Box>
        </Show>
      </Box>
      <Box fontSize="14px" class="leading-[143%] text-[#5d6062]">
        <span class="font-bold">Intacct ID:</span>{' '}
        <span>{props.vendor.accountingSysVendorId}</span>
      </Box>
      <Box fontSize="14px" class="leading-[143%] text-[#5d6062]">
        <span class="font-bold">Service Type(s):</span>{' '}
        <span>{props.vendor.serviceTypes.join(', ')}</span>
      </Box>
      <Show when={canManageBilling()}>
        <Box fontSize="14px" class="leading-[143%] text-[#5d6062]">
          <span class="font-bold">Status:</span>{' '}
          <span>{props.vendor.isDeleted ? 'Inactive' : 'Active'}</span>
        </Box>
      </Show>
    </Stack>
  );
};

export const ProspectItemsRender = (props: ProspectType) => {
  return (
    <Typography variant="caption" class="text-[#333]!">
      {props.name}
    </Typography>
  );
};

export const OfficeItemsRender = (props: OfficeType) => {
  return (
    <Stack width={'100%'}>
      <Stack direction="row" spacing={0.5} justifyContent="space-between">
        <Typography variant="caption" class="font-bold!">
          {props.name}
        </Typography>
        <Typography
          variant="caption"
          class={
            props.active ? '' : 'text-red-600 font-semibold px-1 bg-[#f7dbdc]!'
          }
        >
          {props.active ? '' : 'Inactive'}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={0.5}>
        <AccountIcon class="text-[16px]! align-baseline!" />
        <Typography variant="caption" class="text-[#333]!">
          Manager: {props.manager}
        </Typography>
        <Typography variant="caption" class="text-[#333]!">
          {formatPhoneNumber(props.phone!)}
        </Typography>
      </Stack>

      <Typography variant="caption" class="text-[#333]!">
        {props.address1}
      </Typography>
      <Typography variant="caption" class="text-[#333]!">
        {props.address2}
      </Typography>
      <Stack direction="row" spacing={1}>
        <Typography variant="caption" class="text-[#333]!">
          {props.city},
        </Typography>
        <Typography variant="caption" class="text-[#333]!">
          {props.state}
        </Typography>
        <Typography variant="caption" class="text-[#333]!">
          {props.zip}
        </Typography>
      </Stack>
    </Stack>
  );
};

export const UserItemsRender = (props: UserType) => {
  const image = props.thumbnail;
  const relativeThumbnail = extractRelativePath(image);
  return (
    <Stack direction="row" spacing={2} alignItems="flex-start" class="w-full">
      <Avatar src={userDetailsUrl.img(relativeThumbnail)} size="md" />
      <Stack class="w-full">
        <Stack
          direction={'row'}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="caption" class="font-bold!">
            {props.name}
          </Typography>
          <Typography
            variant="caption"
            sxProps={{
              color: !props.active ? 'red' : '',
              fontWeight: 'bold',
            }}
          >
            {!props.active ? 'Inactive' : ''}
          </Typography>
        </Stack>
        <Typography variant="caption" class="text-[#333]!">
          Office: {props.office} - {props.accountingGroup}
        </Typography>
        <Typography variant="caption" class="text-[#333]!">
          Email: {props.email}
        </Typography>
        <Typography variant="caption" class="text-[#333]!">
          Phone: {formatPhoneNumber(props.phone)}
        </Typography>
      </Stack>
    </Stack>
  );
};

export const TrackingItemsRender = (props: TrackingType) => {
  return (
    <Typography variant="caption" class="font-bold!">
      Order #{props.orderId}
    </Typography>
  );
};

export const FactoringCompanyItemsRender = (props: FactoringCompanyType) => {
  return (
    <Typography variant="caption" class="text-[#333]!">
      {props.name}
    </Typography>
  );
};

export const PhoneItemsRender = (props: PhoneType) => {
  const [anchorEl, setAnchorEl] = createSignal<Element | null>(null);
  return (
    <Stack class="w-full">
      <Stack
        direction={'row'}
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="caption" class="font-bold!">
          Contact Name -{props.contactName}
        </Typography>
        <Typography variant="caption" class="font-bold!">
          {props.contactType}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={0.5}>
        <DomainIcon class="text-[16px]!  align-baseline!" />
        <Typography variant="caption" class="text-[#333]!">
          {props.groupName}
        </Typography>
      </Stack>
      <Stack direction={'row'} alignItems="center" spacing={1}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {props.matchType === 'Fax' ? (
            <FaxIcon class="text-[16px]! align-baseline!" />
          ) : (
            <PhoneIcon class="text-[16px]! align-baseline!" />
          )}
          <Typography variant="caption" class="text-[#333]!">
            {props.displayNumber}
          </Typography>
        </Stack>
        {props.contactType == 'Carrier' && (
          <>
            <img
              class="h-[16px]!"
              src={props.verificationStatus == 2 ? PointyCheck : PointyX}
              onMouseEnter={(event: { currentTarget: Element }) =>
                setAnchorEl(event.currentTarget)
              }
              onMouseLeave={() => setAnchorEl(null)}
            />
            <Popover
              id="mouse-over-popover"
              sx={{ 'pointer-events': 'none' }}
              open={Boolean(anchorEl())}
              anchorEl={anchorEl()}
              dataTestId={`carrier-verification-popover-${props.contactName}-${props.displayNumber}`}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              onClose={() => setAnchorEl(null)}
              disableRestoreFocus
            >
              <Typography variant="body2" class="p-2 max-w-[280px]">
                {props.verificationStatus == 2
                  ? 'Highway Verified Contact'
                  : 'Unverified Contact: Manually added to ATG and not verified'}
              </Typography>
            </Popover>
          </>
        )}
      </Stack>
    </Stack>
  );
};

export const ProItemsRender = (props: ProType) => {
  return (
    <Stack>
      <Typography variant="caption" class="font-bold!">
        Order #{props.orderId}
      </Typography>
      <Typography variant="caption" class="text-[#333]!">
        {props.customerName}
      </Typography>
      <Typography variant="caption" class="text-[#333]!">
        Pro: {props.referenceNumber ?? props.proNumber}
      </Typography>
    </Stack>
  );
};

export const PickupItemsRender = (props: PickupType) => {
  return (
    <Stack>
      <Typography variant="caption" class="font-bold!">
        Order #{props.orderId}
      </Typography>
      <Typography variant="caption" class="text-[#333]!">
        {props.customerName}
      </Typography>
      <Typography variant="caption" class="text-[#333]!">
        Pickup: {props.referenceNumber}
      </Typography>
    </Stack>
  );
};
